import client from "@/api/openapi-ts";
import { usePlugins } from "./helpers/usePlugins";
import { ref } from "vue";
import { defineStore, acceptHMRUpdate } from "pinia";
import slugify from "slugify";
import {
  InputGeo,
  InputCallsign,
  InputDemographic,
  OutputRateCard,
  PreviewRateCard,
} from "@/types/api/RateCard";
import { PaginatedData, PaginationArgs } from "@/types/api/common";

// `/api/v1/rate_card` (GET takes query params) and the `POST /api/v1/rate_card/
// {output_rate_card_id}` operation aren't described in the generated OpenAPI
// client, so reach them through a permissively-typed view of the shared client
// (this keeps the auth/401 middleware intact).
type UntypedResponse = {
  data?: unknown;
  error?: unknown;
  response: Response;
};
const untypedClient = client as unknown as {
  GET: (path: string, init?: unknown) => Promise<UntypedResponse>;
  POST: (path: string, init?: unknown) => Promise<UntypedResponse>;
};

type PendingTaskId = {
  task_id: string;
};

type Geographies = {
  ref_id: number;
  type: string;
};

type GeographyMappings = {
  rate_card_market: string;
  mapped_geography: {
    ref_id: number;
  };
};

type DemographicMappings = {
  rate_card_market: string;
  mapped_demographic: {
    age_from: string;
    age_to: string;
    demo_type: string;
    group: string;
    id: string;
  };
};

type RateCard = {
  name: string;
  description: string;
  index_meta_audience_id: number;
  meta_audience_ids: number[];
  months: string[];
  input_rate_card_id: number;
  geography_mappings: GeographyMappings[];
  callsign_mappings: InputCallsign[];
  demographic_mappings: DemographicMappings[];
};

export const useRateCardStore = defineStore("rateCard", () => {
  const uploading = ref(false);
  const uploadTaskId = ref<string>();
  const uploadError = ref();
  const inputFileName = ref<string>();
  const inputRateCardId = ref<number>();
  const loadingGeos = ref(false);
  const inputGeos = ref<InputGeo[]>();
  const loadingInputCallsigns = ref(false);
  const loadingCallsigns = ref(false);
  const inputCallsigns = ref<InputCallsign[]>();
  const callsignTaskId = ref<string>();
  const callsigns = ref<string[]>();
  const loadingDemographics = ref(false);
  const inputDemographics = ref<InputDemographic[]>();
  const geographyMappings = ref<GeographyMappings[]>();
  const demographicMappings = ref<DemographicMappings[]>();
  const creatingRateCard = ref(false);
  const loadingOutputRateCards = ref(false);
  const outputRateCards = ref<PaginatedData<OutputRateCard>>();
  const selectedOutputRateCard = ref<OutputRateCard>();
  const downloadingRateCard = ref(false);
  const downloadError = ref();
  const previewTaskId = ref<string>();
  const loadingPreview = ref(false);
  const preview = ref<PreviewRateCard>();
  const previewDemoType = ref<string>();

  const { $ws } = usePlugins();

  return {
    uploading,
    uploadTaskId,
    uploadError,
    inputFileName,
    inputRateCardId,
    loadingGeos,
    inputGeos,
    loadingDemographics,
    inputDemographics,
    inputCallsigns,
    loadingInputCallsigns,
    loadingCallsigns,
    callsignTaskId,
    callsigns,
    geographyMappings,
    demographicMappings,
    creatingRateCard,
    loadingOutputRateCards,
    outputRateCards,
    selectedOutputRateCard,
    downloadingRateCard,
    downloadError,
    previewTaskId,
    loadingPreview,
    preview,
    previewDemoType,
    reset() {
      uploadError.value = undefined;
      inputRateCardId.value = undefined;
      inputFileName.value = undefined;
      inputGeos.value = undefined;
      geographyMappings.value = undefined;
      inputCallsigns.value = undefined;
      callsigns.value = undefined;
      inputDemographics.value = undefined;
      demographicMappings.value = undefined;
    },
    async uploadRateCard(file: FormData, fileName: string) {
      uploading.value = true;
      uploadError.value = undefined;
      try {
        const { data, error } = await client.POST("/api/v1/rate_card/upload", {
          body: file as never,
        });
        if (error) {
          throw error;
        }
        const taskId = (data as PendingTaskId | undefined)?.task_id;
        uploadTaskId.value = taskId;
        inputFileName.value = fileName;
        if ($ws && taskId) {
          $ws.subscribe({
            channel: "RateCard",
            payload: {
              id: taskId,
            },
          });
        }
      } catch (err: any) {
        uploadTaskId.value = undefined;
        uploadError.value = err?.message;
        uploading.value = false;
      }
    },
    async getInputGeographies() {
      loadingGeos.value = true;
      const { data } = await client.GET(
        "/api/v1/rate_card/geographies/{input_rate_card_id}",
        {
          params: { path: { input_rate_card_id: inputRateCardId.value! } },
        },
      );
      loadingGeos.value = false;
      inputGeos.value = data as InputGeo[] | undefined;
    },
    async getInputCallsigns() {
      loadingInputCallsigns.value = true;
      const { data } = await client.GET(
        "/api/v1/rate_card/callsigns_map/{input_rate_card_id}",
        {
          params: { path: { input_rate_card_id: inputRateCardId.value! } },
        },
      );
      loadingInputCallsigns.value = false;
      inputCallsigns.value = data as InputCallsign[] | undefined;
    },
    async getCallsigns(geographies: Geographies) {
      loadingCallsigns.value = true;
      try {
        const { data, error } = await client.POST(
          "/api/v1/rate_card/callsigns",
          {
            body: { geographies } as never,
          },
        );
        if (error) {
          throw error;
        }
        const taskId = (data as PendingTaskId | undefined)?.task_id;
        callsignTaskId.value = taskId;
        if ($ws && taskId) {
          $ws.subscribe({
            channel: "RateCard",
            payload: {
              id: taskId,
            },
          });
        }
      } catch {
        loadingCallsigns.value = false;
      }
    },
    async getInputDemographics() {
      loadingDemographics.value = true;
      try {
        const { data, error } = await client.GET(
          "/api/v1/rate_card/demographics/{input_rate_card_id}",
          {
            params: { path: { input_rate_card_id: inputRateCardId.value! } },
          },
        );
        if (error) {
          throw error;
        }
        loadingDemographics.value = false;
        inputDemographics.value = data as InputDemographic[] | undefined;
      } catch (err: any) {
        loadingDemographics.value = false;
        uploadError.value = err?.message;
      }
    },
    async createRateCard(payload: RateCard) {
      creatingRateCard.value = true;
      const { data, error } = await client.POST("/api/v1/rate_card/create", {
        body: payload as never,
      });
      creatingRateCard.value = false;
      if (error) {
        return error;
      }
      return data as OutputRateCard;
    },
    async getRateCards(args: PaginationArgs) {
      loadingOutputRateCards.value = true;
      const { data, response } = await untypedClient.GET("/api/v1/rate_card", {
        params: { query: args },
      });
      loadingOutputRateCards.value = false;
      outputRateCards.value = {
        pagination: {
          page: Number(response.headers.get("current-page")),
          page_items: Number(response.headers.get("page-items")),
          total_pages: Number(response.headers.get("total-pages")),
          total_items: Number(response.headers.get("total-count")),
        },
        data: (data as OutputRateCard[] | undefined) ?? [],
        args,
      };
    },
    async getRateCard(outputRateCardId: number) {
      const { data } = await untypedClient.POST(
        `/api/v1/rate_card/${outputRateCardId}`,
      );
      selectedOutputRateCard.value = data as OutputRateCard | undefined;
    },
    async downloadRateCard(outputRateCardFileId: number) {
      downloadingRateCard.value = true;
      try {
        const { data, error } = await client.GET(
          "/api/v1/rate_card/download/{output_rate_card_file_id}",
          {
            params: {
              path: { output_rate_card_file_id: outputRateCardFileId },
            },
          },
        );
        if (error) {
          throw error;
        }
        const file = data as { contents: BlobPart; filename: string };
        const url = window.URL.createObjectURL(new Blob([file.contents]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", file.filename);
        document.body.appendChild(link);
        link.click();
        downloadingRateCard.value = false;
      } catch (err: any) {
        downloadingRateCard.value = false;
        downloadError.value = err?.message;
      }
    },
    async downloadZip(outputRateCardId: number, outputRateCardName: string) {
      downloadingRateCard.value = true;
      const { data } = await client.GET(
        "/api/v1/rate_card/{output_rate_card_id}/download",
        {
          params: { path: { output_rate_card_id: outputRateCardId } },
          parseAs: "arrayBuffer",
        },
      );
      const blob = new Blob([data as ArrayBuffer], {
        type: "application/octet-stream",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${slugify(outputRateCardName)}.zip`);
      document.body.appendChild(link);
      link.click();
      downloadingRateCard.value = false;
    },
    async previewRateCard(outputRateCardFileId: number) {
      loadingPreview.value = true;
      const { data } = await client.GET(
        "/api/v1/rate_card/preview/{output_rate_card_file_id}",
        {
          params: { path: { output_rate_card_file_id: outputRateCardFileId } },
        },
      );
      const taskId = (data as PendingTaskId | undefined)?.task_id;
      previewTaskId.value = taskId;
      if ($ws && taskId) {
        $ws.subscribe({
          channel: "RateCard",
          payload: {
            id: taskId,
          },
        });
      }
    },
    deleteRateCard(outputRateCardId: number) {
      return client.DELETE("/api/v1/rate_card/{output_rate_card_id}", {
        params: { path: { output_rate_card_id: outputRateCardId } },
      });
    },
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useRateCardStore, import.meta.hot));
}
